import {announce} from '@github-ui/aria-live'
import {MarkdownViewer} from '@github-ui/markdown-viewer'
import {editBlobPath} from '@github-ui/paths'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {sanitize} from '@github-ui/sanitize'
import {useStickyHeader} from '@github-ui/use-sticky-header/useStickyHeader'
import {AlertIcon, ChevronDownIcon, KebabHorizontalIcon, PencilIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Flash, IconButton} from '@primer/react'
import type {ReactNode} from 'react'
import {Suspense, useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'
import {graphql, useLazyLoadQuery} from 'react-relay'

import type {IssuePlanBodyQuery as IssuePlanBodyQueryType} from './__generated__/IssuePlanBodyQuery.graphql'
import styles from './IssuePlanBody.module.css'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssuePlanBodyQuery = graphql`
  query IssuePlanBodyQuery($id: ID!) {
    node(id: $id) {
      ... on Issue {
        plan {
          body
          bodyHTML
          filePath
          branch
          errorMessage
        }
        repository {
          nameWithOwner
        }
      }
    }
  }
`

export const PLAN_BODY_POLL_INTERVAL_MS = 5000
const STICKY_HEADER_OFFSET_PX = 56

export type PlanData = {
  planBody: string
  editUrl?: string
}

type IssuePlanBodyProps = {
  issueId: string
  renderAction?: (data: PlanData) => ReactNode
}

function IssuePlanBodyContent({issueId, renderAction}: IssuePlanBodyProps) {
  const [fetchKey, setFetchKey] = useState(0)

  useEffect(() => {
    const id = setInterval(() => {
      if (!document.hidden) {
        setFetchKey(k => k + 1)
      }
    }, PLAN_BODY_POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [])

  const data = useLazyLoadQuery<IssuePlanBodyQueryType>(
    IssuePlanBodyQuery,
    {id: issueId},
    fetchKey > 0 ? {fetchKey, fetchPolicy: 'store-and-network'} : undefined,
  )
  const plan = data.node?.plan
  const planBody = plan?.body
  const planBodyHTML = plan?.bodyHTML
  const planErrorMessage = plan?.errorMessage

  if (planErrorMessage) {
    return <IssuePlanErrorView errorMessage={planErrorMessage} />
  }

  if (!planBodyHTML || !planBody) {
    return null
  }

  const [owner, ...repoSegments] = data.node?.repository?.nameWithOwner?.split('/') ?? []
  const repo = repoSegments.join('/')
  const editUrl =
    plan?.filePath && plan?.branch && owner && repo
      ? editBlobPath({
          owner,
          repo,
          commitish: plan.branch,
          filePath: plan.filePath,
        })
      : undefined

  const renderedAction = renderAction?.({planBody, editUrl})

  return <IssuePlanBodyView planBodyHTML={planBodyHTML} editUrl={editUrl} renderedAction={renderedAction} />
}

function IssuePlanErrorView({errorMessage}: {errorMessage: string}) {
  useEffect(() => {
    announce(errorMessage)
  }, [errorMessage])

  return (
    <div className={styles.issuePlanBody} data-testid="issue-plan-body-error">
      <div className={styles.header}>
        <span className={styles.summaryMain}>
          <span className={styles.title}>Implementation plan</span>
        </span>
      </div>
      <div className={styles.content}>
        <Flash variant="warning" data-testid="issue-plan-error">
          <AlertIcon size={16} aria-hidden="true" />
          {errorMessage}
        </Flash>
      </div>
    </div>
  )
}

type IssuePlanBodyViewProps = {
  planBodyHTML: string
  editUrl?: string
  renderedAction?: ReactNode
}

function PlanActionMenu({editUrl}: {editUrl: string}) {
  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <IconButton size="small" icon={KebabHorizontalIcon} variant="invisible" aria-label="Plan actions" />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay>
        <ActionList>
          <ActionList.LinkItem href={editUrl}>
            <ActionList.LeadingVisual>
              <PencilIcon />
            </ActionList.LeadingVisual>
            Edit markdown
          </ActionList.LinkItem>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

function IssuePlanBodyView({planBodyHTML, editUrl, renderedAction}: IssuePlanBodyViewProps) {
  const [isCardOpen, setIsCardOpen] = useState(true)
  const [isFullyExpanded, setIsFullyExpanded] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const contentId = useId()

  // Extract the h1 title and strip it from the content as read-only string work,
  // then sanitize LAST so contentHTML is a freshly-minted sanitized value (SSR-safe, no DOM APIs).
  const {planTitle, contentHTML} = useMemo(() => {
    // Detect the leading h1 against the raw string; title text is used as plain text only.
    const h1Match = planBodyHTML.match(/<h1[^>]*>(.*?)<\/h1>/is)
    let title = ''
    let contentSource = planBodyHTML

    if (h1Match) {
      let titleText: string = h1Match[1] ?? ''
      // Loop to fully strip nested/broken tags (avoids CodeQL incomplete-sanitization warning)
      let prev = ''
      while (prev !== titleText) {
        prev = titleText
        titleText = titleText.replace(/<[^>]+>/g, '')
      }
      titleText = titleText.trim()
      const isPlanTitle = /plan/i.test(titleText)
      if (isPlanTitle) {
        title = titleText
        contentSource = planBodyHTML.replace(h1Match[0], '').trim()
      }
    }

    return {planTitle: title, contentHTML: sanitize(contentSource)}
  }, [planBodyHTML])

  const {isSticky, hasRoots, sensorRef} = useStickyHeader()
  const headerRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const contentNodeRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useCallback((node: HTMLDivElement | null) => {
    contentNodeRef.current = node
    if (node) {
      setIsOverflowing(node.scrollHeight > node.clientHeight)
    }
  }, [])

  useEffect(() => {
    const node = contentNodeRef.current
    if (node) {
      setIsOverflowing(node.scrollHeight > node.clientHeight)
    }
  }, [contentHTML])
  const scrollOffset = isSticky ? -STICKY_HEADER_OFFSET_PX : undefined

  // Scroll to header on card collapse — same pattern as NestedListViewHeaderTitle
  useEffect(() => {
    if (scrollOffset !== undefined && headerRef.current && !isCardOpen) {
      const top = headerRef.current.getBoundingClientRect().top + window.scrollY + scrollOffset
      window.scrollTo({top, behavior: 'instant'})
    }
  }, [isCardOpen, scrollOffset])

  // Scroll to card top on content collapse — use cardRef (not sticky) for true position
  const toggleFullyExpanded = useCallback(() => {
    if (isFullyExpanded && cardRef.current) {
      const top = cardRef.current.getBoundingClientRect().top + window.scrollY - STICKY_HEADER_OFFSET_PX
      window.scrollTo({top, behavior: 'instant'})
    }
    setIsFullyExpanded(prev => !prev)
  }, [isFullyExpanded])

  const toggleCard = useCallback(() => {
    setIsCardOpen(prev => !prev)
  }, [])

  return (
    <div
      ref={cardRef}
      className={styles.issuePlanBody}
      data-testid="issue-plan-body"
      style={{'--issue-plan-sticky-header-offset': `${STICKY_HEADER_OFFSET_PX}px`}}
    >
      <div ref={sensorRef} className={styles.sensorBox} />
      <div
        ref={headerRef}
        className={`${styles.header} ${isSticky && hasRoots && isCardOpen ? styles.headerSticky : ''}`}
      >
        <button
          type="button"
          className={styles.summaryButton}
          data-testid="issue-plan-summary"
          aria-expanded={isCardOpen}
          aria-controls={contentId}
          onClick={toggleCard}
        >
          <span className={styles.summaryMain}>
            <span className={isCardOpen ? styles.chevron : styles.chevronCollapsed}>
              <ChevronDownIcon size={16} />
            </span>
            <span className={styles.title}>{planTitle || 'Implementation plan'}</span>
          </span>
        </button>
        {renderedAction || editUrl ? (
          <div className={styles.summaryAction} data-testid="plan-actions">
            {renderedAction}
            {editUrl ? <PlanActionMenu editUrl={editUrl} /> : null}
          </div>
        ) : null}
      </div>

      {isCardOpen ? (
        <>
          <div
            ref={contentRef}
            id={contentId}
            className={isFullyExpanded ? styles.content : styles.contentCollapsed}
            inert={!isFullyExpanded ? true : undefined}
          >
            <MarkdownViewer verifiedHTML={contentHTML} />
          </div>
          {isOverflowing || isFullyExpanded ? (
            <button
              type="button"
              className={styles.expandButton}
              aria-expanded={isFullyExpanded}
              aria-controls={contentId}
              onClick={toggleFullyExpanded}
            >
              {isFullyExpanded ? 'Collapse plan' : 'View full plan'}
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

export function IssuePlanBody({issueId, renderAction}: IssuePlanBodyProps) {
  return (
    <ErrorBoundary boundaryName="IssuePlanBody" fallback={null} critical={false}>
      <Suspense fallback={null}>
        <IssuePlanBodyContent issueId={issueId} renderAction={renderAction} />
      </Suspense>
    </ErrorBoundary>
  )
}
